import { useEffect } from "react";

const INCOMING_CALL_RING_SRC = '/sounds/incoming_call.mp3';
/** Not message notification — alternate call ring if primary 404. */
const INCOMING_CALL_RING_FALLBACK_SRC = '/sounds/whatsapp.mp3';

function isVoiceRemoteMediaElement(audio) {
  return audio?.id === 'voice-remote-media';
}

class RingtoneManager {
  constructor() {
    this.currentAudio = null;
    this.currentSrc = null;
    this.componentCount = 0;
    this.isPlaying = false;
    this.allAudioInstances = new Set();
    this.userHasInteracted = false;
    this.pendingPlaySrc = null;
    /** Bumps on intentional stop so stale error handlers cannot start fallback. */
    this.playSessionId = 0;
    this.currentErrorHandler = null;

    document.addEventListener('click', () => {
      this.userHasInteracted = true;
      this.resumePendingPlay();
    }, { once: true, capture: true });
  }

  resumePendingPlay() {
    if (!this.pendingPlaySrc || this.componentCount <= 0) {
      this.pendingPlaySrc = null;
      return;
    }
    const src = this.pendingPlaySrc;
    this.pendingPlaySrc = null;
    this.play(src, { loop: true, volume: 0.7 });
  }

  addComponent() {
    this.componentCount += 1;
  }

  removeComponent() {
    this.componentCount = Math.max(0, this.componentCount - 1);
    if (this.componentCount === 0) {
      this.stopOwnAudio();
    }
  }

  detachErrorHandler(audio) {
    if (!audio || !this.currentErrorHandler) return;
    try {
      audio.removeEventListener('error', this.currentErrorHandler);
    } catch {
      // ignore
    }
    this.currentErrorHandler = null;
  }

  play(src, options = {}) {
    const { loop = true, volume = 0.7 } = options;

    if (this.isPlaying && this.currentSrc === src) {
      return;
    }

    this.stopOwnAudio();

    const sessionId = this.playSessionId + 1;
    this.playSessionId = sessionId;
    const primarySrc = src;

    const startPlayback = (resolvedSrc, isFallback = false) => {
      if (sessionId !== this.playSessionId) return;

      try {
        const audio = new Audio(resolvedSrc);
        this.currentAudio = audio;
        this.currentAudio.loop = loop;
        this.currentAudio.volume = volume;
        this.currentSrc = resolvedSrc;
        this.allAudioInstances.add(audio);

        this.currentAudio.addEventListener('ended', () => {
          if (!loop) {
            this.isPlaying = false;
          }
        });

        const onError = () => {
          if (sessionId !== this.playSessionId) return;
          if (!isFallback && resolvedSrc === primarySrc && primarySrc !== INCOMING_CALL_RING_FALLBACK_SRC) {
            console.warn('[voice-ring] primary missing, trying fallback', INCOMING_CALL_RING_FALLBACK_SRC);
            this.detachErrorHandler(audio);
            this.stopOwnAudio({ preserveSessionId: true });
            startPlayback(INCOMING_CALL_RING_FALLBACK_SRC, true);
            return;
          }
          console.warn('[voice-ring] audio file error', resolvedSrc);
          this.stopOwnAudio();
        };

        this.currentErrorHandler = onError;
        audio.addEventListener('error', onError);

        const tryPlay = () => {
          if (sessionId !== this.playSessionId || this.currentAudio !== audio) return;
          audio.play().then(() => {
            if (sessionId !== this.playSessionId || this.currentAudio !== audio) return;
            this.isPlaying = true;
            console.info('[voice-ring] playing', resolvedSrc);
          }).catch((error) => {
            if (sessionId !== this.playSessionId || this.currentAudio !== audio) return;
            const message = error?.message ?? String(error);
            const interrupted = error?.name === 'AbortError'
              || /interrupted by a call to pause/i.test(message);
            if (interrupted) return;
            console.warn('[voice-ring] play blocked:', message);
            if (this.componentCount > 0) {
              this.pendingPlaySrc = resolvedSrc;
            }
          });
        };

        tryPlay();

        if (!this.userHasInteracted) {
          const startOnGesture = () => {
            this.userHasInteracted = true;
            tryPlay();
          };
          ['pointerdown', 'keydown', 'touchstart', 'click'].forEach((eventName) => {
            document.addEventListener(eventName, startOnGesture, { once: true, capture: true });
          });
        }
      } catch (error) {
        console.error('[voice-ring] create Audio failed:', error);
        this.stopOwnAudio();
      }
    };

    startPlayback(src, false);
  }

  stopOwnAudio(options = {}) {
    const { preserveSessionId = false } = options;
    if (!preserveSessionId) {
      this.playSessionId += 1;
    }
    this.pendingPlaySrc = null;

    if (this.currentAudio) {
      this.detachErrorHandler(this.currentAudio);
      try {
        this.currentAudio.pause();
        this.currentAudio.currentTime = 0;
        this.currentAudio.loop = false;
        this.currentAudio.src = '';
        this.currentAudio.load();
      } catch (e) {
        console.log('🎵 Error deteniendo currentAudio:', e);
      }
      this.currentAudio = null;
    }

    this.allAudioInstances.forEach((audio) => {
      try {
        if (!audio || isVoiceRemoteMediaElement(audio)) return;
        audio.pause();
        // eslint-disable-next-line no-param-reassign
        audio.currentTime = 0;
        // eslint-disable-next-line no-param-reassign
        audio.loop = false;
        // eslint-disable-next-line no-param-reassign
        audio.src = '';
        audio.load();
      } catch (e) {
        console.log('🎵 Error deteniendo instancia rastreada:', e);
      }
    });
    this.allAudioInstances.clear();

    this.currentSrc = null;
    this.isPlaying = false;
  }

  forceStopAll() {
    this.stopOwnAudio();
    const allPageAudios = document.querySelectorAll('audio');
    allPageAudios.forEach((audio, index) => {
      if (isVoiceRemoteMediaElement(audio)) return;
      try {
        if (!audio.paused) {
          audio.pause();
          // eslint-disable-next-line no-param-reassign
          audio.currentTime = 0;
        }
      } catch (e) {
        console.log(`🎵 Error deteniendo audio #${index}:`, e);
      }
    });
  }

  stop() {
    this.stopOwnAudio();
  }
}

const ringtoneManager = new RingtoneManager();

function bindEarlyUserGestureForAudio() {
  const markInteracted = () => {
    ringtoneManager.userHasInteracted = true;
    ringtoneManager.resumePendingPlay?.();
  };
  ['pointerdown', 'keydown', 'touchstart', 'click'].forEach((eventName) => {
    document.addEventListener(eventName, markInteracted, { once: true, capture: true });
  });
}

if (typeof document !== 'undefined') {
  bindEarlyUserGestureForAudio();
}

export function playIncomingCallRingtone(src = INCOMING_CALL_RING_SRC) {
  ringtoneManager.play(src, { loop: true, volume: 0.7 });
}

export function stopIncomingCallRingtone() {
  ringtoneManager.stopOwnAudio();
}

window.stopRingtone = () => {
  stopIncomingCallRingtone();
};

window.playIncomingCallRingtone = playIncomingCallRingtone;

window.killAllAudio = () => {
  const allAudios = document.querySelectorAll('audio');
  allAudios.forEach((audio) => {
    if (isVoiceRemoteMediaElement(audio)) return;
    audio.pause();
    // eslint-disable-next-line no-param-reassign
    audio.currentTime = 0;
    // eslint-disable-next-line no-param-reassign
    audio.src = '';
    audio.load();
  });
};

export default function useRingtone(src, options = {}) {
  const loop = options.loop !== false;
  const volume = typeof options.volume === 'number' ? options.volume : 0.7;
  const enabled = options.enabled !== false;

  useEffect(() => {
    if (!src || !enabled) {
      return undefined;
    }

    ringtoneManager.addComponent();
    ringtoneManager.play(src, { loop, volume });

    return () => {
      ringtoneManager.removeComponent();
    };
  }, [src, loop, volume, enabled]);
}
